// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { smallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import Printer from "@insite/mobius/Icons/Printer";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Edit from "@rugby/XDL/Icons/Edit";
import { color } from "@rugby/XDL/Utilities";
import { css } from "styled-components";

interface OrderHeaderStyle {
    titleText?: TypographyProps;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: GridContainerProps;
    buttonStyle?: InjectableCss;
}

export const orderHeaderStyle: OrderHeaderStyle = {
    titleText: {
        css: css`
            margin: 50px 0 40px 0;

            @media print {
                font-size: 36px !important;
            }
        `,
        variant: "h2",
        as: "h1",
    },
    buttonsHiddenContainer: {
        css: css`
            margin: 55px 0 35px 0;
            display: flex;
            justify-content: flex-end;
        `,
        below: "lg",
    },
    menuHiddenContainer: {
        css: css`
            margin: 55px 0 35px 0;
        `,
        above: "md",
    },
    headerGridContainer: {
        css: css`
            div[class^="GridOffset"] {
                width: auto;
            }
        `,
    },
    buttonStyle: {
        css: css`
            &:focus {
                outline: none;
                box-shadow: 0 0 8px 0 ${getColor("secondary")};
            }
        `,
    },
};

interface AddressBlockStyle {
    titleText?: InjectableCss;
    wrapper?: InjectableCss;
    editLink?: LinkPresentationProps;
}

export const addressBlockStyle: AddressBlockStyle = {
    titleText: {
        css: css`
            font-size: 22px;
            margin-bottom: 10px;
            display: block;
            line-height: 32px;
            font-weight: normal;
        `,
    },
    wrapper: {
        css: css`
            [data-test-selector*="companyName"] {
                font-weight: 700;
            }
        `,
    },
    editLink: {
        color: "secondary",
        icon: {
            iconProps: {
                src: Edit,
                color: "secondary",
                size: 14,
            },
        },
        typographyProps: {
            size: 15,
            css: css`
                letter-spacing: 0;
                line-height: 32px;
            `,
        },
    },
};

interface OrderInfoStyle {
    orderWrapper?: any;
    orderGridItems?: GridItemProps;
    titleText?: TypographyProps;
    orderLabel?: InjectableCss;
    orderValue?: InjectableCss;
    mainInformationGridContainer?: InjectableCss;
}

export const orderInfoStyle: OrderInfoStyle = {
    titleText: {
        css: css`
            margin: 50px 0 40px 0;
        `,
        variant: "h2",
        as: "h1",
    },
    orderGridItems: {
        css: css`
            @media print {
                padding: 15px !important;
            }
        `,
    },
    orderWrapper: {
        width: 12,
        css: css`
            display: inline-flex;
            align-items: center;
            margin-bottom: 0;
            flex-direction: row;
        `,
    },
    orderLabel: {
        css: css`
            margin-bottom: 0;
            font-weight: 700;
            font-size: 15px;
            white-space: nowrap;

            &:after {
                content: ":";
            }
        `,
    },
    orderValue: {
        css: css`
            margin-left: 5px;
            font-size: 15px;
        `,
    },
    mainInformationGridContainer: {
        css: css``,
    },
};

interface OrderSummeryTableStyles {
    container?: any;
    totalLabel?: InjectableCss;
    totalValue?: InjectableCss;
    subtotalLabel?: InjectableCss;
    subtotalValue?: InjectableCss;
    hrBorder?: InjectableCss;
    orderSummeryTitle?: InjectableCss;
    orderSummeryTitleGridItem?: any;
    mainWrapper?: InjectableCss;
    hrBorderGridItem?: any;
}

export const orderSummeryTableStyles: OrderSummeryTableStyles = {
    container: {
        gap: 0,
        css: css`
            background: ${color("common.ui3", 0.5)};
            padding: 12px 10px;
            div[class^="GridItemStyle"] {
                padding: 3px 10px;
            }
        `,
    },
    totalLabel: {
        css: css`
            font-size: 18px;
            line-height: 22px;
            font-weight: bold;
        `,
    },
    totalValue: {
        css: css`
            font-size: 18px;
            line-height: 22px;
            font-weight: bold;
            margin-left: auto;
        `,
    },
    subtotalLabel: {
        css: css`
            font-weight: bold;
        `,
    },
    subtotalValue: {
        css: css`
            font-weight: bold;
            margin-left: auto;
        `,
    },
    hrBorder: {
        css: css`
            width: 100%;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    orderSummeryTitle: {
        css: css`
            font-size: 22px;
            display: block;
            font-weight: 400;
            margin-bottom: 20px;
        `,
    },
    orderSummeryTitleGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    mainWrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.border")};
            padding-top: 20px;
        `,
    },
    hrBorderGridItem: { 
        width: 12,
        css: css`
            display: flex;
            align-items: center;
            height: 16px;
        `,
    },
};

interface ButtonsSetStyle {
    printButton?: ButtonPresentationProps;
}

export const buttonsSetStyle: ButtonsSetStyle = {
    printButton: {
        icon: {
            src: Printer,
            position: "left",
        },
        variant: "secondary",
        css: css`
            height: 40px;
        `,
    },
};

smallHeadingAndTextStyles.heading = {
    color: "text.main",
};
